import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import useAPI from '../../../services/useAPI';
import {
  Button, ButtonVariant,
  Stack,
  StackItem,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Drawer,
  DrawerPanelContent,
  DrawerContent,
  DrawerContentBody
} from '@patternfly/react-core';
import _ from 'lodash';
import fp from 'lodash/fp';

import useAlerts from '../../../utils/hooks/useAlerts';
import { NodeRelationshipType } from 'utils/types';
import Tree, { SELECT_MODE } from '../../GraphTree/Tree';
import TreeFuncs, {
  createDataItem,
  pruneEmptyChildren,
  filterTreeNodeTypes,
  filterTreeRelationship,
  addParent,
  matchDescFn,
  addDescFn
} from 'components/GraphTree/TreeFuncs';
import useTreeQuery, {
  treeKeys
} from 'components/GraphTree/queries';
import Tabs from '../../Tabs/Tabs';
import AssetDetail from './AssetDetail';
import { useTranslation } from 'localization/useTranslation';
import Tl from '../../../localization/LocalizationKeys';
import NewAssetNode from './NewAssetDialog';
import useBoolean from '../../../utils/hooks/useBoolean';
import LinkAsset from './LinkAsset';
import LinkDashboard from './LinkDashboard';
import { Icons } from '../../Common';
import { Size } from '../../Common/Icons';
import { NodeType } from 'utils/types';
import createQueryRequest from 'services/createQueryRequest';
import ContentPage from '../../ContentView/ContentPage/ContentPage';
import './AdminEditTreePage.css';
import BreadcrumbSkeleton from 'components/Breadcrumb/BreadcrumbSkeleton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import createMutationRequest from 'utils/createMutationRequest';
import { ResourceType } from 'utils/types';
import { createOrgOwnerResourceRelationQueryData, org2trees } from '../Organizations/Relations/queries';

const findNode = _.curry((nodeId, node) => {
  if (node.id === nodeId) {
    return node;
  }
});

export const TreeName = ({ treeId }) => {
  const query = useTreeQuery(treeId);
  return query.isLoading ? <BreadcrumbSkeleton /> : (query.data?.name ?? '');
};

const AdminEditTreePage = ({ breadcrumbs }) => {
  const T = useTranslation();
  // TODO: use hasError state
  const [activeItem, setActiveItem] = useState({});
  const [alerts, , addErrorAlert] = useAlerts();
  const [newAssetDialogOpen, showNewAssetDialog, hideNewAssetDialog] = useBoolean();
  const { treeId } = useParams();
  const api = useAPI();

  const queryClient = useQueryClient();
  const treeQuery = useTreeQuery(treeId);

  const fetchedTree = useMemo(() => {
    if (!treeQuery.data) {
      return [];
    }
    const newTree = [treeQuery.data].map(
      fp.compose(
        TreeFuncs.map(createDataItem()),
        TreeFuncs.map(pruneEmptyChildren()),
        TreeFuncs.mapDesc(matchDescFn, addDescFn),
        TreeFuncs.filter(filterTreeRelationship([NodeRelationshipType.Contains, NodeRelationshipType.DependsOn, null, undefined])),
        TreeFuncs.filter(filterTreeNodeTypes([NodeType.Node, NodeType.Asset, NodeType.Dashboard])),
        TreeFuncs.mapWithParent(addParent, null)
      ));
    setActiveItem(oldItem => {
      if (oldItem == null) {
        return null;
      }
      return TreeFuncs.findOne(findNode(oldItem.id), newTree[0]);
    });
    return newTree;
  }, [treeQuery.data]);

  const treeOwnerQuery = useQuery({
    queryKey: org2trees.org(createOrgOwnerResourceRelationQueryData(ResourceType.Node, treeId)),
    queryFn: createQueryRequest(api.core.findRelationships),
    enabled: !!treeId
  });

  const orgId = useMemo(() => {
    return _.first(treeOwnerQuery.data)?.subjectId;
  }, [treeOwnerQuery.data]);

  const createNodeQuery = useMutation({
    mutationFn: createMutationRequest(api.core.createNode),
    onSuccess: () => {
      treeQuery.refetch();
    },
    onError: (error) => {
      console.error(error);
      addErrorAlert(T.admin.graphTree.addNode.error);
    }
  });

  const createEdgeQuery = useMutation({
    mutationFn: createMutationRequest(api.core.createEdge),
    onSuccess: () => {
      treeQuery.refetch();
    },
    onError: (error) => {
      console.error(error);
      addErrorAlert(T.admin.graphTree.addEdge.error);
    }
  });

  const deleteEdgeQuery = useMutation({
    mutationFn: createMutationRequest(api.core.deleteEdge),
    onSuccess: () => {
      treeQuery.refetch();
      setActiveItem(null);
    },
    onError: (error) => {
      console.error(error);
      addErrorAlert(T.admin.graphTree.deleteEdge.error);
    }
  });

  const deleteNodeQuery = useMutation({
    mutationFn: createMutationRequest(api.core.deleteNode),
    onSuccess: () => {
      queryClient.invalidateQueries(treeKeys.tree(treeId));
      setActiveItem(null);
    }
  });

  const updateNodeQuery = useMutation({
    mutationFn: createMutationRequest(api.core.updateNode),
    onSuccess: () => {
      queryClient.invalidateQueries(treeKeys.tree(treeId));
    }
  });

  const onLinkItem = useCallback((items) => {
    createEdgeQuery.mutate(items);
  }, [createEdgeQuery]);

  const updateAssetDetails = useCallback((item) => {
    const updateItem = _.pick(item, ['id', 'displayName']);
    updateNodeQuery.mutate(updateItem);
  }, [updateNodeQuery]);
  const isAllTree = treeId === '$all';

  const tabs = useMemo(() => [
    {
      label: Tl.admin.graphTree.tab.information,
      component: AssetDetail,
      props: {
        item: { ...activeItem },
        setItem: updateAssetDetails
      }

    },
    {
      label: Tl.admin.graphTree.tab.link,
      component: LinkAsset,
      props: {
        parentItem: { ...activeItem },
        onLinkAssets: onLinkItem,
        disableLink: isAllTree,
        isLinking: createEdgeQuery.isLoading
      }
    },
    {
      label: Tl.admin.graphTree.tab.dashboard,
      component: LinkDashboard,
      props: {
        parentItem: { ...activeItem },
        onLinkDashboards: onLinkItem,
        disableLink: isAllTree,
        isLinking: createEdgeQuery.isLoading
      }
    }
    // {
    //   label: Tl.admin.graphTree.tab.treeProperties,
    //   component: TreeProperties,
    //   props: {
    //     root: fetchedTree[0],
    //     onSave: _onSaveTreeProperties,
    //     disable: isAllTree
    //   }
    // }
  ], [activeItem, updateAssetDetails, onLinkItem, isAllTree, createEdgeQuery.isLoading]);

  const onNewNodeSave = useCallback((newNode) => {
    const node = { ...newNode, organizationId: orgId };
    createNodeQuery.mutate(node);
    hideNewAssetDialog();
  }, [createNodeQuery, hideNewAssetDialog, orgId]);

  const onTreeSelect = useCallback((item) => {
    setActiveItem(item);
  }, []);

  const deleteNode = useCallback(() => {
    deleteNodeQuery.mutate(activeItem.id);
  }, [activeItem, deleteNodeQuery]);

  const unlinkItem = useCallback(() => {
    deleteEdgeQuery.mutate(activeItem.edge.id);
  }, [activeItem, deleteEdgeQuery]);

  const canDelete = useMemo(() => {
    return (
      !isAllTree &&
      activeItem && activeItem?.parent != null &&
      ((activeItem.parent.type === NodeType.Node && activeItem.type === NodeType.Asset) ||
      activeItem.type === NodeType.Dashboard ||
      (activeItem.type === NodeType.Node && (activeItem.children == null || _.isEmpty(activeItem.children)))
      ));
  }, [activeItem, isAllTree]);

  const canCreateNode = useMemo(() => {
    return (
      activeItem?.type === NodeType.Node
      && !isAllTree
    );
  }, [activeItem, isAllTree]);

  const panelContent = useMemo(() => {
    return (
      <DrawerPanelContent isResizable defaultSize="300px">
        <Tree
          tree={fetchedTree}
          onSelect={onTreeSelect}
          isLoading={treeQuery.isLoading}
          relationFilter={[NodeRelationshipType.Contains]}
          selectMode={SELECT_MODE.BUILD}
        />
      </DrawerPanelContent>
    );
  }, [fetchedTree, onTreeSelect, treeQuery.isLoading]);

  const drawerContent = useMemo(() => {
    return (
      <>
        <Stack>
          <StackItem>
            <Toolbar>
              <ToolbarContent>
                <ToolbarItem >
                  <Button variant={ButtonVariant.primary}
                    isLoading={createNodeQuery.isLoading}
                    onClick={showNewAssetDialog}
                    isDisabled={!canCreateNode}
                  >
                    { T.admin.graphTree.newAsset }
                  </Button>
                </ToolbarItem>
                <ToolbarItem >
                  { (activeItem?.type === NodeType.Node)
                    ? <Button variant={ButtonVariant.danger}
                      isLoading={deleteNodeQuery.isLoading}
                      isDisabled={!canDelete}
                      onClick={deleteNode}
                      icon={<Icons.Delete size={Size.SMALL}/>}
                    >
                      { T.common.delete }
                    </Button>
                    : <Button variant={ButtonVariant.danger}
                      isLoading={deleteEdgeQuery.isLoading}
                      isDisabled={!canDelete}
                      onClick={unlinkItem}
                      icon={<Icons.Unlink size={Size.SMALL}/>}
                    >
                      { T.common.unlink }
                    </Button>
                  }
                </ToolbarItem>
              </ToolbarContent>
            </Toolbar>
          </StackItem>
          <StackItem className={'tab-container'}>
            <Tabs tabs={tabs} tabClassName={'tab-content'}/>
          </StackItem>
        </Stack>
      </>
    );
  }, [T.admin.graphTree.newAsset, T.common.delete, T.common.unlink, activeItem?.type, canCreateNode, canDelete, createNodeQuery.isLoading, deleteEdgeQuery.isLoading, deleteNode, deleteNodeQuery.isLoading, showNewAssetDialog, tabs, unlinkItem]);

  return (
    <ContentPage breadcrumb={breadcrumbs} help={'admin/trees/edit'}>
      { alerts }
      <Drawer isExpanded isInline position="left">
        <DrawerContent panelContent={panelContent}>
          <DrawerContentBody>{ drawerContent }</DrawerContentBody>
        </DrawerContent>
      </Drawer>
      <NewAssetNode
        isOpen={newAssetDialogOpen}
        onSave={onNewNodeSave}
        onCancel={() => hideNewAssetDialog()}
        parent={activeItem}
      />
    </ContentPage>
  );

};

export default React.memo(AdminEditTreePage);
